import {finalize, Observable, MonoTypeOperatorFunction} from "rxjs";
import {standard} from "felles-ng2/støttefunksjoner/nilsjekk";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erListe, somListe} from "felles-ng2/støttefunksjoner/lister";
import {EnEllerFlere} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellHendelseHåndteringBygger";

const debug = initDebugger('DatalagerAbonnenter');


/** Register over aktive abonnementer, dvs aktive subscriptions mot datalager */
export class DatalagerAbonnenter<NØKKEL = string> {

    /** Map som holder rede på hvor mange abonnenter som finnes for en gitt nøkkel, der nøkkel som hovedregel er en entitetId */
    private abonnenter = new Map<NØKKEL, number>();

    constructor(public readonly navn: string) {
    }

    /** Registrerer abonnement representert av angitt 'observerbar',
     * Avregistrer abonnementet når observerbar terminerer.
     * MERK at returnert Observable må benyttes videre av kallende kode og termineres når den ikke lenger benyttes. */
    observer<T>(nøkler: EnEllerFlere<NØKKEL>, observerbar: Observable<T>): Observable<T> {
        this.registrer(nøkler);
        return observerbar.pipe(this.avregistrerVedTermineringAvObserverbar(nøkler));
    }

    //@todo hva er forskjellen mellom denne og observer ?
    observerMange<T>(nøkler: EnEllerFlere<NØKKEL>, observerbar: Observable<T>): Observable<T> {
        const nøkkelSamling = erListe(nøkler) ? nøkler : [nøkler];
        nøkkelSamling.forEach(nøkkel => this.registrer(nøkkel));
        return observerbar.pipe(finalize(() => {
            nøkkelSamling.forEach(nøkkel => this.avregistrer(nøkkel));
        }));
    }

    /** Pipe operator som sørger for å avslutte abonnement på angitte nøkler når den observerbare terminerer. */
    avregistrerVedTermineringAvObserverbar<T>(nøkler: EnEllerFlere<NØKKEL>): MonoTypeOperatorFunction<T> {
        return finalize(() => this.avregistrer(nøkler));
    }

    registrer(nøkler: EnEllerFlere<NØKKEL>) {
        somListe(nøkler).forEach(nøkkel => {
            this.debug(`registrer ${nøkkel}, antall ab etter: ${this.antallAbonnenter(nøkkel) + 1}`);
            this.abonnenter.set(nøkkel, this.antallAbonnenter(nøkkel) + 1);
        });
    }

    avregistrer(nøkler: EnEllerFlere<NØKKEL>) {
        somListe(nøkler).forEach(nøkkel => {
            this.debug(`avregistrer ${nøkkel}, antall ab etter: ${this.antallAbonnenter(nøkkel) - 1}`);
            if (this.antallAbonnenter(nøkkel) == 0) {
                this.debug(`avregistrer kalt når det ikke finnes eksisterende abonnenter for ${nøkkel}`);
                return;
            }
            this.abonnenter.set(nøkkel, this.antallAbonnenter(nøkkel) - 1);
            if (!this.inneholder(nøkkel)) this.abonnenter.delete(nøkkel); //for å unngå at abonnenter vokser evig
        });
    }

    antallAbonnenter(nøkkel: EnEllerFlere<NØKKEL>): number {
        return somListe(nøkkel)
          .map(nøkkel => standard(this.abonnenter.get(nøkkel), 0))
          .reduce((a, b) => a + b, 0);
    }

    inneholder(nøkkel: EnEllerFlere<NØKKEL>): boolean {
        return somListe(nøkkel)
          .some(nøkkel => this.antallAbonnenter(nøkkel) > 0);
    }

    get antallAbonnenterTotalt(): number {
        return Array.from(this.abonnenter.values()).reduce((a, b) => a + b, 0);
    }

    //private hjelpemetoder

    private debug(melding: string) {
        debug(`${this.navn}: ${melding}`);
    }
}
